// /app/brands/$brandId/comments — per-brand comments inbox.
//
// Mailbox-style two-pane layout: list on the left, selected-comment
// detail on the right. The detail pane shows the comment body, any AI
// reply drafts, and per-comment actions (regenerate / edit / approve /
// hide / escalate).
//
// Data source: comments-service v0.1.x — already running in cluster,
// already publishing comment.* events on NATS. Only the dashboard side
// was missing.
//
// Refresh: 30s poll while the page is mounted. SSE inbox stream
// (spec §11 `/api/v1/brands/:id/inbox/stream`) is the v0.2.x upgrade
// path; today the poll cost is one HTTP request per 30s, fine.

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { useI18n } from "../../../lib/i18n";
import {
  ApiError,
  brands as brandsAPI,
  comments as commentsAPI,
  type Brand,
  type Comment,
  type CommentStatus,
  type ReplyDraft,
} from "../../../lib/api";

const STATUS_TABS: { value: CommentStatus | ""; key: string }[] = [
  { value: "",              key: "inbox.tab.all" },
  { value: "new",           key: "inbox.tab.new" },
  { value: "draft_pending", key: "inbox.tab.draftPending" },
  { value: "escalated",     key: "inbox.tab.escalated" },
];

function BrandInbox() {
  const { t, locale } = useI18n();
  const { brandId } = Route.useParams();

  const [brand, setBrand] = useState<Brand | null>(null);
  const [filter, setFilter] = useState<CommentStatus | "">("");
  const [list, setList] = useState<Comment[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Brand name for the header. One-shot fetch.
  useEffect(() => {
    brandsAPI.get(brandId).then(setBrand).catch(() => { /* tolerated */ });
  }, [brandId]);

  function reload() {
    setErr(null);
    commentsAPI.listForBrand(brandId, {
      status: filter || undefined,
      limit: 100,
    })
      .then((r) => {
        setList(r.comments);
        // Auto-select the first item when filter changes, so the
        // right pane isn't empty.
        if (r.comments.length > 0 && (!selectedId || !r.comments.find((c) => c.id === selectedId))) {
          setSelectedId(r.comments[0].id);
        }
        if (r.comments.length === 0) {
          setSelectedId(null);
        }
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : t("common.errorGeneric")));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [brandId, filter]);

  // Light poll for new comments while the page is open.
  useEffect(() => {
    const h = window.setInterval(reload, 30_000);
    return () => window.clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, filter]);

  const selected = useMemo(
    () => (list ?? []).find((c) => c.id === selectedId) ?? null,
    [list, selectedId],
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Link to="/app/brands/$brandId" params={{ brandId }}
              className="text-xs text-muted hover:text-primary ring-nexus rounded inline-block w-fit">
          ← {brand?.trade_name || t("brands.title")}
        </Link>
        <h1 className="font-display text-3xl font-semibold">{t("inbox.title")}</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border-subtle overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        {STATUS_TABS.map((tab) => {
          const active = filter === tab.value;
          return (
            <button
              key={tab.value || "all"}
              type="button"
              onClick={() => setFilter(tab.value)}
              className={[
                "ring-nexus px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors relative",
                active ? "text-primary" : "text-secondary hover:text-primary",
              ].join(" ")}
            >
              {t(tab.key)}
              {active ? (
                <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-nexus-red rounded-t-[2px]" aria-hidden />
              ) : null}
            </button>
          );
        })}
      </div>

      {err ? <p className="text-error text-sm">{err}</p> : null}

      {/* Two-pane mailbox layout */}
      <div className="grid lg:grid-cols-[minmax(0,360px)_1fr] gap-4">
        {/* List pane */}
        <Card className="!p-0 overflow-hidden">
          {list === null ? (
            <p className="text-secondary text-sm p-4">{t("common.loading")}</p>
          ) : list.length === 0 ? (
            <p className="text-secondary text-sm p-4">{t("inbox.empty")}</p>
          ) : (
            <ul className="divide-y divide-border-subtle max-h-[70vh] overflow-y-auto">
              {list.map((c) => {
                const isSel = c.id === selectedId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(c.id)}
                      className={[
                        "ring-nexus w-full text-left p-3 hover:bg-elevated transition-colors flex flex-col gap-1",
                        isSel ? "bg-elevated" : "",
                      ].join(" ")}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-primary truncate font-medium">
                          @{c.author_handle ?? c.author_display_name ?? c.platform_comment_id.slice(0, 8)}
                        </span>
                        <StatusBadge status={c.status} />
                      </div>
                      <p className="text-xs text-secondary line-clamp-2">{c.body}</p>
                      <span className="text-[10px] font-mono uppercase text-muted">
                        {c.platform} · {new Date(c.received_at).toLocaleString(locale === "tr" ? "tr-TR" : "en-US")}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Detail pane */}
        <div className="flex flex-col gap-4">
          {!selected ? (
            <Card><p className="text-secondary text-sm">{t("inbox.pickHint")}</p></Card>
          ) : (
            <CommentDetail
              comment={selected}
              onChanged={(updated) => {
                // Mutate the list in-place so badges update without a re-fetch.
                setList((prev) => prev?.map((c) => c.id === updated.id ? updated : c) ?? prev);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// CommentDetail — right pane. Renders the comment + its AI drafts +
// action buttons. Drafts and per-comment state are fetched lazily via
// GET /v1/comments/{id}.
function CommentDetail({
  comment, onChanged,
}: {
  comment: Comment;
  onChanged: (c: Comment) => void;
}) {
  const { t } = useI18n();
  const [drafts, setDrafts] = useState<ReplyDraft[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null); // draft.id being edited
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function reload() {
    setErr(null);
    commentsAPI.get(comment.id)
      .then((r) => setDrafts(r.drafts))
      .catch((e) => setErr(e instanceof ApiError ? e.message : t("common.errorGeneric")));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [comment.id]);

  async function regen(extra: string) {
    setBusy(true); setErr(null);
    try {
      await commentsAPI.regenerateDraft(comment.id, extra ? { extra_instructions: extra } : {});
      reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setBusy(false);
    }
  }
  async function hide() {
    setBusy(true); setErr(null);
    try {
      const updated = await commentsAPI.hide(comment.id);
      onChanged(updated);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally { setBusy(false); }
  }
  async function escalate() {
    setBusy(true); setErr(null);
    try {
      const updated = await commentsAPI.escalate(comment.id);
      onChanged(updated);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally { setBusy(false); }
  }
  async function saveEdit(replyId: string, text: string) {
    setBusy(true); setErr(null);
    try {
      await commentsAPI.editDraft(replyId, { draft_text: text });
      setEditing(null);
      reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally { setBusy(false); }
  }
  async function approve(replyId: string) {
    setBusy(true); setErr(null);
    try {
      await commentsAPI.approveDraft(replyId);
      reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally { setBusy(false); }
  }

  return (
    <>
      {/* Header card */}
      <Card>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary">
                @{comment.author_handle ?? comment.author_display_name ?? "—"}
              </span>
              <StatusBadge status={comment.status} />
              <span className="text-xs font-mono uppercase text-muted">{comment.platform}</span>
            </div>
            {comment.category ? (
              <span className="text-xs text-muted">
                {t("inbox.category")}: <span className="font-mono">{comment.category}</span>
                {comment.confidence != null ? ` · ${Math.round(comment.confidence * 100)}%` : ""}
              </span>
            ) : null}
            <span className="text-[10px] font-mono uppercase text-muted">
              {new Date(comment.received_at).toLocaleString()}
            </span>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="ghost" type="button" onClick={() => void hide()}
                    disabled={busy || comment.status === "hidden"} className="!text-secondary">
              🙈 {t("inbox.hide")}
            </Button>
            <Button variant="ghost" type="button" onClick={() => void escalate()}
                    disabled={busy || comment.status === "escalated"} className="!text-warning">
              ⚠️ {t("inbox.escalate")}
            </Button>
          </div>
        </div>
        <p className="mt-3 whitespace-pre-wrap text-primary">{comment.body}</p>
      </Card>

      {/* Drafts */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-semibold">{t("inbox.drafts.title")}</h2>
          <Button variant="secondary" type="button" onClick={() => void regen("")} loading={busy}>
            🤖 {t("inbox.drafts.generate")}
          </Button>
        </div>
        {err ? <p className="text-error text-sm mb-3">{err}</p> : null}
        {drafts === null ? (
          <p className="text-secondary text-sm">{t("common.loading")}</p>
        ) : drafts.length === 0 ? (
          <p className="text-secondary text-sm">{t("inbox.drafts.empty")}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {drafts.map((d) => (
              <li key={d.id} className={[
                "border rounded-lg p-3 flex flex-col gap-2",
                d.approved_at ? "border-success bg-success/5" : "border-border-default",
              ].join(" ")}>
                <div className="flex items-center gap-2 text-[11px] font-mono text-muted">
                  <span>{d.drafted_by === "ai" ? "🤖 AI" : d.drafted_by}</span>
                  {d.ai_model ? <><span>·</span><span>{d.ai_model}</span></> : null}
                  {d.cost_cents > 0 ? <><span>·</span><span>{(d.cost_cents / 100).toFixed(2)}¢</span></> : null}
                  {d.sent_at ? <><span>·</span><span className="text-success">✓ {t("inbox.drafts.sent")}</span></> : null}
                  {!d.sent_at && d.approved_at ? <><span>·</span><span className="text-success">✓ {t("inbox.drafts.approved")}</span></> : null}
                </div>
                {editing === d.id ? (
                  <EditDraftForm
                    initial={d.draft_text}
                    busy={busy}
                    onCancel={() => setEditing(null)}
                    onSave={(text) => void saveEdit(d.id, text)}
                  />
                ) : (
                  <p className="whitespace-pre-wrap text-sm text-primary">{d.draft_text}</p>
                )}
                {!d.approved_at && editing !== d.id ? (
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" type="button" onClick={() => setEditing(d.id)} disabled={busy}>
                      ✏️ {t("inbox.drafts.edit")}
                    </Button>
                    <Button variant="primary" type="button" onClick={() => void approve(d.id)} disabled={busy}>
                      ✓ {t("inbox.drafts.approve")}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function EditDraftForm({
  initial, busy, onCancel, onSave,
}: {
  initial: string;
  busy: boolean;
  onCancel: () => void;
  onSave: (text: string) => void;
}) {
  const { t } = useI18n();
  const [text, setText] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(text.trim()); }} className="flex flex-col gap-2" noValidate>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        maxLength={2000}
        className="ring-nexus bg-elevated text-primary border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>{t("inbox.drafts.cancel")}</Button>
        <Button variant="primary" type="submit" loading={busy}>{t("inbox.drafts.save")}</Button>
      </div>
    </form>
  );
}

function StatusBadge({ status }: { status: CommentStatus }) {
  const { t } = useI18n();
  const tone =
    status === "escalated" ? "text-error border-error/40 bg-error/10"
      : status === "hidden" ? "text-muted border-muted/40 bg-muted/10"
      : status === "approved" || status === "sent" ? "text-success border-success/40 bg-success/10"
      : status === "draft_pending" ? "text-info border-info/40 bg-info/10"
      : "text-warning border-warning/40 bg-warning/10"; // new, classifying
  return (
    <span className={`text-[10px] font-mono uppercase border px-1.5 py-0.5 rounded ${tone}`}>
      {t(`inbox.status.${status}`)}
    </span>
  );
}

export const Route = createFileRoute("/app/brands/$brandId_/comments")({
  component: BrandInbox,
});
